import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, ArrowLeft } from "lucide-react";

export default function AdminForgotPassword() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    if (!email) return toast.error("Enter your email");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/request-reset", { email });
      setStatusMsg(data.message || "If an account exists for this email, a reset code has been sent.");
      setStage("code");
      toast.success("Check your email for a 6-digit reset code");
    } catch {
      toast.error("Could not send a reset code right now. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    if (e) e.preventDefault();
    if (!code || code.length < 6) return toast.error("Enter the full 6-digit code");
    if (!newPassword || newPassword.length < 8) return toast.error("New password must be at least 8 characters");
    setLoading(true);
    try {
      await api.post("/auth/admin/reset-password", { email, code, new_password: newPassword });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err, "Reset failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <form
          onSubmit={stage === "email" ? requestCode : submitReset}
          className="w-full max-w-md kcis-card p-8 sm:p-10 kcis-fade-up"
          data-testid="admin-forgot-password-form"
        >
          <div className="h-12 w-12 rounded-2xl bg-purple-50 text-purple-900 flex items-center justify-center mb-5">
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="kcis-overline text-purple-900">Practitioner access</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">
            {stage === "email" ? "Reset your password" : "Enter your reset code"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {stage === "email"
              ? "Enter your admin email. If we find an account, we'll send you a 6-digit code that expires in 15 minutes."
              : `We sent a 6-digit code to ${email} if the account exists. Enter it below along with your new password.`}
          </p>

          {stage === "email" ? (
            <div className="mt-7 space-y-4">
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input
                  data-testid="admin-reset-email"
                  type="email"
                  autoComplete="email"
                  className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12"
                data-testid="admin-reset-request-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset code"}
              </Button>
              <div className="text-center">
                <Link to="/admin/login" className="text-xs text-slate-500 hover:text-purple-900" data-testid="admin-reset-back-to-login">
                  <ArrowLeft className="h-3 w-3 inline mr-1" /> Back to sign-in
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-7 space-y-4">
              {statusMsg && (
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 flex items-start gap-2 text-sm text-emerald-900">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{statusMsg}</span>
                </div>
              )}
              <div>
                <Label className="text-sm text-slate-700">6-digit reset code</Label>
                <Input
                  data-testid="admin-reset-code"
                  className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12 text-lg tracking-[0.4em] font-mono"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-slate-700">New password</Label>
                <Input
                  data-testid="admin-reset-new-password"
                  type="password"
                  autoComplete="new-password"
                  className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                />
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12"
                data-testid="admin-reset-submit"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set new password"}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => { setStage("email"); setCode(""); setNewPassword(""); setStatusMsg(""); }}
                  className="text-slate-500 hover:text-purple-900"
                >
                  ← Use a different email
                </button>
                <button
                  type="button"
                  onClick={() => requestCode()}
                  disabled={loading}
                  className="text-purple-900 hover:underline disabled:opacity-50"
                >
                  Resend code
                </button>
              </div>
            </div>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
}
